import { drinkingDayWindow, type DrinkEvent, type Participant, type SessionBootstrap } from '@lbc/shared';

import { GLOBAL_DEFAULT_BEER_ABV, classifyDrink, type DrinkCategory } from '../../domain/drinks';

export const LBC_RELEASE_DATE = '2026-07-26';
export const WRAPPED_FIRST_MONTHLY_DATE = '2026-09-01';

export type WrappedDrinkCategory = DrinkCategory;

/** Presentation-only context attached while combining events from several groups. */
export type WrappedDrinkEvent = DrinkEvent & {
  wrappedTimezone?: string;
  wrappedDayBoundaryHour?: number;
};

export interface WrappedMonth {
  key: string;
  label: string;
  drinks: number;
  drinkingDays: number;
}
export interface WrappedCategoryCount {
  name: WrappedDrinkCategory;
  value: number;
}

export interface WrappedParticipantStats {
  participant: Participant;
  drinks: number;
  drinkingDays: number;
  longestStreak: number;
  longestBreak: number;
}

export interface WrappedStats {
  events: DrinkEvent[];
  totalDrinks: number;
  drinkingDays: number;
  averagePerDrinkingDay: number;
  longestStreak: number;
  longestBreak: number;
  favoriteCategory: WrappedDrinkCategory | null;
  categories: WrappedCategoryCount[];
  months: WrappedMonth[];
  /** Complete group history for charts; monthly stats keep `months` target-scoped. */
  chartMonths: WrappedMonth[];
  participantStats: WrappedParticipantStats[];
  strongestMonth: WrappedMonth | null;
  quietestMonth: WrappedMonth | null;
  noDrinkDays: number;
}

export interface WrappedMonthMetrics {
  totalDrinks: number;
  drinkingDays: number;
  averagePerDrinkingDay: number;
  longestStreak: number;
  longestBreak: number;
  favoriteCategory: WrappedDrinkCategory | null;
  noDrinkDays: number;
}

export interface WrappedMonthlyQuote {
  speaker: string;
  text: string;
}

export interface WrappedMonthlyComparison {
  monthKey: string;
  monthLabel: string;
  previousMonthKey: string;
  previousMonthLabel: string;
  current: WrappedMonthMetrics;
  previous: WrappedMonthMetrics;
  quote: WrappedMonthlyQuote;
}

export function isMonthlyWrappedAvailable(timezone: string, at = new Date()): boolean {
  const parts = localDateTimeParts(at, timezone);
  const localDate = `${parts.year}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`;
  if (localDate < WRAPPED_FIRST_MONTHLY_DATE) return false;
  return localDate > WRAPPED_FIRST_MONTHLY_DATE || parts.hour >= 6;
}

export function isAnnualWrappedAvailable(timezone: string, at = new Date()): boolean {
  const parts = localDateTimeParts(at, timezone);
  return parts.month === 12 && parts.day === 31 && parts.hour >= 14;
}

export function wrappedMonthlyTargetMonthKey(
  timezone: string,
  at = new Date(),
): string {
  const currentMonthKey = monthKey(at, timezone);
  return previousMonthKey(currentMonthKey);
}

export function wrappedAnnualPeriodKey(timezone: string, at = new Date()): string {
  return String(localDateTimeParts(at, timezone).year);
}

export function wrappedButtonLabel(timezone: string, at = new Date()): string {
  if (isAnnualWrappedAvailable(timezone, at)) {
    return `LBC Wrapped: ${wrappedAnnualPeriodKey(timezone, at)}`;
  }
  const targetMonth = wrappedMonthlyTargetMonthKey(timezone, at);
  return `LBC Wrapped: ${monthLabel(targetMonth, timezone).replace(/\s+\d{4}$/, '')}`;
}

export function createWrappedMonthlyComparison(
  bootstrap: SessionBootstrap,
  events: ReadonlyArray<WrappedDrinkEvent> = bootstrap.drinkEvents,
  referenceDate = new Date(),
  targetMonthKey = wrappedMonthlyTargetMonthKey(bootstrap.group.timezone, referenceDate),
): WrappedMonthlyComparison {
  const monthKeyValue = targetMonthKey;
  const previousMonthKeyValue = previousMonthKey(monthKeyValue);
  const activeEvents = events.filter(
    (event) =>
      event.deletedAt === null &&
      Number.isFinite(Date.parse(event.occurredAt)) &&
      Date.parse(event.occurredAt) >= Date.parse(`${LBC_RELEASE_DATE}T00:00:00Z`),
  );
  const currentEvents = activeEvents.filter(
    (event) => eventMonthKey(event, bootstrap.group.timezone, bootstrap.group.dayBoundaryHour) === monthKeyValue,
  );
  const previousEvents = activeEvents.filter(
    (event) =>
      eventMonthKey(event, bootstrap.group.timezone, bootstrap.group.dayBoundaryHour) ===
      previousMonthKeyValue,
  );
  const current = monthMetrics(
    currentEvents,
    bootstrap.group.timezone,
    bootstrap.group.dayBoundaryHour,
    monthKeyValue,
    referenceDate,
    bootstrap.group.defaultAbv,
  );
  const previous = monthMetrics(
    previousEvents,
    bootstrap.group.timezone,
    bootstrap.group.dayBoundaryHour,
    previousMonthKeyValue,
    referenceDate,
    bootstrap.group.defaultAbv,
  );

  return {
    monthKey: monthKeyValue,
    monthLabel: monthLabel(monthKeyValue, bootstrap.group.timezone),
    previousMonthKey: previousMonthKeyValue,
    previousMonthLabel: monthLabel(previousMonthKeyValue, bootstrap.group.timezone),
    current,
    previous,
    quote: monthlyQuote(current, previous),
  };
}

export function createWrappedStats(
  bootstrap: SessionBootstrap,
  events: ReadonlyArray<WrappedDrinkEvent> = bootstrap.drinkEvents,
): WrappedStats {
  const activeEvents = events
    .filter(
      (event) =>
        event.deletedAt === null &&
        Number.isFinite(Date.parse(event.occurredAt)) &&
        Date.parse(event.occurredAt) >= Date.parse(`${LBC_RELEASE_DATE}T00:00:00Z`),
    )
    .sort((left, right) => Date.parse(left.occurredAt) - Date.parse(right.occurredAt));
  const categories = countCategories(activeEvents, bootstrap.group.defaultAbv);
  const months = countMonths(activeEvents, bootstrap.group.timezone, bootstrap.group.dayBoundaryHour);
  const participantStats = bootstrap.participants
    .map((participant) => {
      const participantEvents = activeEvents.filter((event) => event.participantId === participant.id);
      const dayKeys = getDayKeys(
        participantEvents,
        bootstrap.group.timezone,
        bootstrap.group.dayBoundaryHour,
      );
      return {
        participant,
        drinks: participantEvents.length,
        drinkingDays: dayKeys.length,
        longestStreak: longestConsecutiveDays(dayKeys),
        longestBreak: longestBreakDays(dayKeys),
      };
    })
    .sort((left, right) => right.drinks - left.drinks || right.drinkingDays - left.drinkingDays);
  const dayKeys = getDayKeys(
    activeEvents,
    bootstrap.group.timezone,
    bootstrap.group.dayBoundaryHour,
  );

  return {
    events: activeEvents,
    totalDrinks: activeEvents.length,
    drinkingDays: dayKeys.length,
    averagePerDrinkingDay: dayKeys.length === 0 ? 0 : activeEvents.length / dayKeys.length,
    longestStreak: longestConsecutiveDays(dayKeys),
    longestBreak: longestBreakDays(dayKeys),
    favoriteCategory: categories[0]?.name ?? null,
    categories,
    months,
    chartMonths: months,
    participantStats,
    strongestMonth: months.reduce<WrappedMonth | null>(
      (strongest, month) => (strongest === null || month.drinks > strongest.drinks ? month : strongest),
      null,
    ),
    quietestMonth: months.reduce<WrappedMonth | null>(
      (quietest, month) => (quietest === null || month.drinks < quietest.drinks ? month : quietest),
      null,
    ),
    noDrinkDays: countNoDrinkDays(
      dayKeys,
      bootstrap.group.timezone,
      bootstrap.group.dayBoundaryHour,
    ),
  };
}

export function createWrappedMonthlyStats(
  bootstrap: SessionBootstrap,
  targetMonthKey: string,
  events: ReadonlyArray<WrappedDrinkEvent> = bootstrap.drinkEvents,
  referenceDate = new Date(),
): WrappedStats {
  const activeEvents = events.filter(
    (event) =>
      event.deletedAt === null &&
      Number.isFinite(Date.parse(event.occurredAt)) &&
      Date.parse(event.occurredAt) >= Date.parse(`${LBC_RELEASE_DATE}T00:00:00Z`) &&
      eventMonthKey(event, bootstrap.group.timezone, bootstrap.group.dayBoundaryHour) === targetMonthKey,
  );
  const stats = createWrappedStats(bootstrap, activeEvents);
  const chartEvents = events.filter(
    (event) =>
      event.deletedAt === null &&
      Number.isFinite(Date.parse(event.occurredAt)) &&
      Date.parse(event.occurredAt) >= Date.parse(`${LBC_RELEASE_DATE}T00:00:00Z`),
  );
  const metrics = monthMetrics(
    activeEvents,
    bootstrap.group.timezone,
    bootstrap.group.dayBoundaryHour,
    targetMonthKey,
    referenceDate,
    bootstrap.group.defaultAbv,
  );
  const month = {
    key: targetMonthKey,
    label: monthLabel(targetMonthKey, bootstrap.group.timezone),
    drinks: stats.totalDrinks,
    drinkingDays: stats.drinkingDays,
  };
  return {
    ...stats,
    months: [month],
    chartMonths: countMonths(chartEvents, bootstrap.group.timezone, bootstrap.group.dayBoundaryHour),
    strongestMonth: month,
    quietestMonth: month,
    noDrinkDays: metrics.noDrinkDays,
  };
}

export function categoryForEvent(
  event: Pick<DrinkEvent, 'volumeMl' | 'abv'>,
  beerAbv = GLOBAL_DEFAULT_BEER_ABV,
): WrappedDrinkCategory {
  return classifyDrink(event, beerAbv);
}

function countCategories(
  events: ReadonlyArray<WrappedDrinkEvent>,
  beerAbv: number,
): WrappedCategoryCount[] {
  const counts = new Map<WrappedDrinkCategory, number>();
  for (const event of events) {
    const category = categoryForEvent(event, beerAbv);
    counts.set(category, (counts.get(category) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([name, value]) => ({ name, value }))
    .sort((left, right) => right.value - left.value);
}

function monthMetrics(
  events: ReadonlyArray<WrappedDrinkEvent>,
  timezone: string,
  dayBoundaryHour: number,
  periodMonthKey: string,
  referenceDate: Date,
  defaultAbv: number,
): WrappedMonthMetrics {
  const dayKeys = getDayKeys(events, timezone, dayBoundaryHour);
  const periodStart = `${periodMonthKey}-01`;
  const [year = Number.NaN, month = Number.NaN] = periodMonthKey.split('-').map(Number);
  const lastDay = Number.isFinite(year) && Number.isFinite(month)
    ? new Date(Date.UTC(year, month, 0)).getUTCDate()
    : 0;
  const currentMonthKey = monthKey(referenceDate, timezone);
  const referenceParts = localDateTimeParts(referenceDate, timezone);
  const periodEnd =
    periodMonthKey === currentMonthKey
      ? `${periodMonthKey}-${String(referenceParts.day).padStart(2, '0')}`
      : `${periodMonthKey}-${String(lastDay).padStart(2, '0')}`;
  const releaseDay = formatDateKey(
    drinkingDayWindow(new Date(`${LBC_RELEASE_DATE}T12:00:00Z`), timezone, dayBoundaryHour).start,
    timezone,
  );
  const startDay = periodMonthKey === monthKey(new Date(LBC_RELEASE_DATE), timezone)
    ? releaseDay
    : periodStart;
  const coveredDays = Math.max(0, dayDifference(startDay, periodEnd) + 1);
  return {
    totalDrinks: events.length,
    drinkingDays: dayKeys.length,
    averagePerDrinkingDay: dayKeys.length === 0 ? 0 : events.length / dayKeys.length,
    longestStreak: longestConsecutiveDays(dayKeys),
    longestBreak: longestBreakDays(dayKeys),
    favoriteCategory: countCategories(events, defaultAbv)[0]?.name ?? null,
    noDrinkDays: Math.max(0, coveredDays - dayKeys.length),
  };
}

function monthlyQuote(
  current: WrappedMonthMetrics,
  previous: WrappedMonthMetrics,
): WrappedMonthlyQuote {
  const drinkDelta = current.totalDrinks - previous.totalDrinks;
  if (current.totalDrinks === 0) {
    return { speaker: 'Olaf Scholz', text: 'Im Großen und Ganzen: nichts.' };
  }
  if (current.drinkingDays >= 10 && drinkDelta >= 0) {
    return { speaker: 'Boris Pistorius', text: 'An zahlreichen Tagen einsatzbereit. Auftrag erfüllt.' };
  }
  if (current.averagePerDrinkingDay >= 5) {
    return { speaker: 'Jens Spahn', text: 'Die Versorgungslage war durchgehend stabil.' };
  }
  if (current.longestBreak >= 7) {
    return { speaker: 'Robert Habeck', text: 'Eine bemerkenswerte Phase der Nicht-Eskalation.' };
  }
  if (drinkDelta >= 3) {
    return { speaker: 'Christian Lindner', text: 'Die Getränkebilanz wächst. Der Markt reagiert positiv.' };
  }
  if (drinkDelta <= -3) {
    return { speaker: 'Olaf Scholz', text: 'Im Großen und Ganzen: weniger.' };
  }
  if (drinkDelta === 0) {
    return { speaker: 'Friedrich Merz', text: 'Stabilität ist auch eine Form von Wachstum.' };
  }
  return { speaker: 'Katharina Reiche', text: 'Die Versorgung war gesichert.' };
}

function countMonths(
  events: ReadonlyArray<WrappedDrinkEvent>,
  timezone: string,
  dayBoundaryHour: number,
): WrappedMonth[] {
  const counts = new Map<string, { drinks: number; days: Set<string> }>();
  for (const event of events) {
    const eventContext = wrappedEventContext(event, timezone, dayBoundaryHour);
    const day = drinkingDayWindow(new Date(event.occurredAt), eventContext.timezone, eventContext.dayBoundaryHour).start;
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: eventContext.timezone,
      year: 'numeric',
      month: '2-digit',
    }).formatToParts(day);
    const year = parts.find((part) => part.type === 'year')?.value ?? '0000';
    const month = parts.find((part) => part.type === 'month')?.value ?? '01';
    const key = `${year}-${month}`;
    const dayKey = formatDateKey(day, eventContext.timezone);
    const current = counts.get(key) ?? { drinks: 0, days: new Set<string>() };
    current.drinks += 1;
    current.days.add(dayKey);
    counts.set(key, current);
  }
  const start = new Date(`${LBC_RELEASE_DATE}T12:00:00Z`);
  const end = new Date();
  const startKey = monthKey(start, timezone);
  const endKey = monthKey(end, timezone);
  for (const key of monthKeysBetween(startKey, endKey)) {
    if (!counts.has(key)) counts.set(key, { drinks: 0, days: new Set<string>() });
  }
  return [...counts.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, value]) => ({
      key,
      label: new Intl.DateTimeFormat('de-DE', {
        month: 'short',
        year: 'numeric',
        timeZone: timezone,
      }).format(new Date(`${key}-01T12:00:00Z`)),
      drinks: value.drinks,
      drinkingDays: value.days.size,
    }));
}

function getDayKeys(events: ReadonlyArray<WrappedDrinkEvent>, timezone: string, dayBoundaryHour: number): string[] {
  return [
    ...new Set(
      events.map((event) => {
        const context = wrappedEventContext(event, timezone, dayBoundaryHour);
        const start = drinkingDayWindow(new Date(event.occurredAt), context.timezone, context.dayBoundaryHour).start;
        return formatDateKey(start, context.timezone);
      }),
    ),
  ].sort();
}

function eventMonthKey(event: WrappedDrinkEvent, timezone: string, dayBoundaryHour: number): string {
  const context = wrappedEventContext(event, timezone, dayBoundaryHour);
  const day = drinkingDayWindow(new Date(event.occurredAt), context.timezone, context.dayBoundaryHour).start;
  return monthKey(day, context.timezone);
}

interface WrappedEventContext {
  timezone: string;
  dayBoundaryHour: number;
}

function wrappedEventContext(
  event: WrappedDrinkEvent,
  timezone: string,
  dayBoundaryHour: number,
): WrappedEventContext {
  return {
    timezone: event.wrappedTimezone ?? timezone,
    dayBoundaryHour: event.wrappedDayBoundaryHour ?? dayBoundaryHour,
  };
}

function formatDateKey(at: Date, timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(at);
  const year = parts.find((part) => part.type === 'year')?.value ?? '0000';
  const month = parts.find((part) => part.type === 'month')?.value ?? '01';
  const day = parts.find((part) => part.type === 'day')?.value ?? '01';
  return `${year}-${month}-${day}`;
}

function countNoDrinkDays(
  dayKeys: ReadonlyArray<string>,
  timezone: string,
  dayBoundaryHour: number,
): number {
  if (dayKeys.length === 0) return 0;
  const releaseDay = formatDateKey(
    drinkingDayWindow(new Date(`${LBC_RELEASE_DATE}T12:00:00Z`), timezone, dayBoundaryHour).start,
    timezone,
  );
  const today = formatDateKey(
    drinkingDayWindow(new Date(), timezone, dayBoundaryHour).start,
    timezone,
  );
  const coveredDays = Math.max(0, dayDifference(releaseDay, today) + 1);
  const activeDays = dayKeys.filter((key) => key >= releaseDay && key <= today).length;
  return Math.max(0, coveredDays - activeDays);
}

function monthKey(at: Date, timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(at);
  const year = parts.find((part) => part.type === 'year')?.value ?? '0000';
  const month = parts.find((part) => part.type === 'month')?.value ?? '01';
  return `${year}-${month}`;
}

function previousMonthKey(key: string): string {
  const [year = Number.NaN, month = Number.NaN] = key.split('-').map(Number);
  if (!Number.isFinite(year) || !Number.isFinite(month)) return key;
  const previous = new Date(Date.UTC(year, month - 2, 15));
  return `${previous.getUTCFullYear()}-${String(previous.getUTCMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(key: string, timezone: string): string {
  return new Intl.DateTimeFormat('de-DE', {
    month: 'long',
    year: 'numeric',
    timeZone: timezone,
  }).format(new Date(`${key}-01T12:00:00Z`));
}

function localDateTimeParts(at: Date, timezone: string): {
  year: number;
  month: number;
  day: number;
  hour: number;
} {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(at);
  const value = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? '0');
  return { year: value('year'), month: value('month'), day: value('day'), hour: value('hour') };
}

function monthKeysBetween(startKey: string, endKey: string): string[] {
  const [startYear = Number.NaN, startMonth = Number.NaN] = startKey.split('-').map(Number);
  const [endYear = Number.NaN, endMonth = Number.NaN] = endKey.split('-').map(Number);
  if (!Number.isFinite(startYear) || !Number.isFinite(startMonth)) return [];
  const keys: string[] = [];
  let cursor = startYear * 12 + startMonth - 1;
  const end = endYear * 12 + endMonth - 1;
  while (cursor <= end) {
    const year = Math.floor(cursor / 12);
    const month = (cursor % 12) + 1;
    keys.push(`${year}-${String(month).padStart(2, '0')}`);
    cursor += 1;
  }
  return keys;
}

function longestConsecutiveDays(dayKeys: ReadonlyArray<string>): number {
  if (dayKeys.length === 0) return 0;
  let longest = 1;
  let current = 1;
  for (let index = 1; index < dayKeys.length; index += 1) {
    if (dayDifference(dayKeys[index - 1]!, dayKeys[index]!) === 1) {
      current += 1;
      longest = Math.max(longest, current);
    } else {
      current = 1;
    }
  }
  return longest;
}

function longestBreakDays(dayKeys: ReadonlyArray<string>): number {
  return longestBreakDaysWithin(dayKeys);
}

function longestBreakDaysWithin(
  dayKeys: ReadonlyArray<string>,
  startDay?: string,
  endDay?: string,
): number {
  if (dayKeys.length === 0) {
    return startDay !== undefined && endDay !== undefined ? Math.max(0, dayDifference(startDay, endDay) + 1) : 0;
  }
  let longest = 0;
  if (startDay !== undefined) longest = Math.max(longest, dayDifference(startDay, dayKeys[0]!) );
  for (let index = 1; index < dayKeys.length; index += 1) {
    longest = Math.max(longest, dayDifference(dayKeys[index - 1]!, dayKeys[index]!) - 1);
  }
  if (endDay !== undefined) longest = Math.max(longest, dayDifference(dayKeys[dayKeys.length - 1]!, endDay));
  return longest;
}

function dayDifference(left: string, right: string): number {
  return Math.round((Date.parse(`${right}T00:00:00Z`) - Date.parse(`${left}T00:00:00Z`)) / 86_400_000);
}
